/**
 * MOCK · CANONICAL (CT-5286) — the rows behind a dynamic profile tab.
 *
 * A dynamic tab lists data with filters and search. Rows use the SAME
 * normalized card contract as the entity list (title / visual / description /
 * badges / meta), so one card component serves both screens.
 *
 * Filter definitions come down WITH the rows: which filters a tab offers is the
 * tenant's business, not the frontend's, so there is no filter list in code.
 *
 * The customers Activity tab is NOT here: it is its own feed with its own shape
 * (CT-5527) and lives in `activity.mock.js`. The companies Activity tab is still
 * a generic LIST, so its rows stay here.
 *
 * Coverage:
 *   companies/activity      activity rows for the companies LIST tab
 *   vehicles/garage         service records, filtered by status
 *   vehicles/appointments   a tab with nothing in it yet — the empty state
 */

const garageRow = (i, status, title, description) => ({
  entity_id: `svc-${i}`,
  title,
  description,
  badges: [{ label: status, variant: status === 'Completed' ? 'success' : 'warning' }],
  meta: [{ label: 'Order', value: `WO-${1000 + i}` }],
  filter_values: { status: status.toLowerCase() },
});

const activityRow = (i, type, author, title, description) => ({
  entity_id: `act-${i}`,
  title,
  description,
  badges: [{ label: type, variant: type === 'Call' ? 'informative' : 'neutral' }],
  meta: [{ label: 'By', value: author }],
  filter_values: { type: type.toLowerCase(), author: author.toLowerCase().replace(' ', '-') },
});

const buildSeed = () => ({
  'companies/activity': {
    filters: [
      {
        id: 'type',
        label: 'Type',
        options: [
          { value: 'call', label: 'Call' },
          { value: 'email', label: 'Email' },
          { value: 'note', label: 'Note' },
        ],
      },
    ],
    items: [
      activityRow(1, 'Call', 'Dana Ruiz', 'Annual planning call', 'Reviewed the account plan for next year.'),
      activityRow(2, 'Email', 'Sam Ortiz', 'Contract renewal draft', 'Shared the draft agreement with procurement.'),
      activityRow(3, 'Note', 'Dana Ruiz', 'Org change', 'New head of operations starts next month.'),
    ],
  },
  'vehicles/garage': {
    filters: [
      {
        id: 'status',
        label: 'Status',
        options: [
          { value: 'completed', label: 'Completed' },
          { value: 'in progress', label: 'In progress' },
        ],
      },
    ],
    items: [
      garageRow(1, 'Completed', 'Oil and filter change', 'Routine 20k service.'),
      garageRow(2, 'Completed', 'Brake pad replacement', 'Front axle, both sides.'),
      garageRow(3, 'In progress', 'Transmission diagnostic', 'Intermittent shift fault under load.'),
    ],
  },
  'vehicles/appointments': { filters: [], items: [] },
});

let mockTabData = buildSeed();

export const getMockTabData = () => mockTabData;

/** Test-only: reset the mutable seed between tests within a file. */
export const __resetMockTabDataForTests = () => {
  mockTabData = buildSeed();
};
